import type { NpcHullTier, StarSystem } from "./types";
import { generateHaulingMission, type HaulingMission } from "./haulingMissions";

// Accommodation footprint per VIP, by mission tier (16.27) -- carved
// directly out of the ship's existing Cargo stat, not a new module and not
// a separate crew/passenger slot count.
export const VIP_FOOTPRINT_M3: Record<NpcHullTier, number> = {
  T1: 10,
  T2: 20,
  T3: 35,
  T4: 60
};

export const MIN_VIPS = 1;
export const MAX_VIPS = 3;

// Max VIPs acceptable = floor(ship's current Cargo / that tier's
// footprint), 1-3 per mission (16.27).
export function maxVips(cargoM3: number, hullTier: NpcHullTier): number {
  return Math.min(MAX_VIPS, Math.floor(cargoM3 / VIP_FOOTPRINT_M3[hullTier]));
}

export interface VipTransportMission extends HaulingMission {
  vipCount: number;
  footprintM3: number;
  cargoUsedM3: number; // vipCount x footprintM3, reserved out of Cargo for the mission's duration
}

// Generates a VIP Transport mission -- reuses Hauling's (16.26) exact
// route/reward formula directly (16.27: "no separate pricing invented"),
// adding only the passenger mechanic on top. Returns null (gating
// generation rather than offering an impossible mission) if the ship's
// current cargo can't fit even 1 VIP at this tier's footprint.
export function generateVipTransportMission(
  origin: StarSystem,
  destination: StarSystem,
  hullTier: NpcHullTier,
  cargoM3: number,
  systems: StarSystem[]
): VipTransportMission | null {
  const vipCount = maxVips(cargoM3, hullTier);
  if (vipCount < MIN_VIPS) {
    return null;
  }

  const hauling = generateHaulingMission(origin, destination, hullTier, systems);
  const footprintM3 = VIP_FOOTPRINT_M3[hullTier];

  return {
    ...hauling,
    vipCount,
    footprintM3,
    cargoUsedM3: vipCount * footprintM3
  };
}
